
import { Vue, Component, Prop } from "vue-property-decorator";
import { v4 as uuid } from "uuid";
import storage from "@/services/storage";
import splitFileNameExt from "@/utils/splitFileNameExt";

import UPDATE_RESUME from "@/apollo/profile/resume/UpdateResume.gql";
import GET_RESUME from "@/apollo/profile/resume/GetResume.gql";
import CREATE_AFFINDA_PROFILE from "@/apollo/profile/affinda/CreateAffindaProfile.gql"
import GET_AFFINDA_PROFILE from "@/apollo/profile/affinda/GetAffindaProfile.gql"
import DELETE_AFFINDA_PROFILE from "@/apollo/profile/affinda/DeleteAffindaProfile.gql"

import UiButton from "@/components/button/ui-button.vue";
import FormWrapper from "@/components/form/FormWrapper.vue";
import FileIcon from "@/components/icons/FileIcon.vue";

import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import { FileSizeRestrictions } from "@/entities/FileSizeRestrictions";

import AffindaClient from "@/services/resumeScraping/affinda";
import UserResume from "@/entities/UserResume";

interface HTMLInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

@Component({
  components: {
    UiButton,
    FormWrapper,
    FileIcon,
  },
  apollo: {
    resume: {
      query: GET_RESUME,
      variables() {
        return {
          id: this.userId,
        };
      },
      update: (data) => {
        return data.user_profile_by_pk.resume;
      },
    },
  },
})
export default class ProfileResume extends Vue {
  @Prop({ type: String, required: true }) readonly userId!: string;

  resume: string = "";
  affindaClient: AffindaClient = new AffindaClient();

  get filename(): string {
    if (!this.resume) {
      return "";
    }

    const regex = new RegExp("[^.]+$");
    const extension = this.resume.match(regex);
    const name = this.resume
      .split(".")
      .slice(0, -1)
      .join(".")
      .substring(0, this.resume.length - 41);
    
    return `${name}.${extension}`;
  }

  handleFileUpload(): void {
    (this.$refs.fileUpload as HTMLInputElement).click();
  }

  validateFileTypes(file) {
   const allowedTypes = [
        'application/msword', // DOC
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
        'application/pdf' // PDF
      ];


    if (!allowedTypes.includes(file.type))  {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(`Invalid file format. Please upload a PDF or DOC file.`)
      );
        return false;
      }
    return true;
  };

  validateFileSize(file) {
      const filesize = file.size / 1024 / 1024;
      if (filesize > FileSizeRestrictions.DOCUMENT_MAX) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(`The maximum allowed file size is ${FileSizeRestrictions.DOCUMENT_MAX} MB`)
      );
        return false;
      }
    return true;
  };

  async changeFiles(event: HTMLInputEvent): Promise<void> {
    const files = event.target.files;

    if (!files) {
      return;
    }

    const file = files[0];
    
    // Validate file size
    if (!this.validateFileSize(file)) {
      return;
    }
    // validate file type
    if (!this.validateFileTypes(file)) {
      return;
    }

    // Add file to Affinda and get the parsed data
    const data = await this.affindaClient.createResume(file);

    // Emit event to update the user resume data on frontend
    this.$eventBus.emit(UserResume.UPDATE_RESUME, data)

    // Upload the file to S3
    await this.uploadFile(file);
  }

  async handleRemoveFile(): Promise<void> {

    // [qtrinh@ideatheorem.com] We also need to check and then delete if docuemnt exists
    const userAffindaProfileResponse = await this.$apollo.query(
      {
        query: GET_AFFINDA_PROFILE,
        variables: {
          user_id: this.userId
        }
      }
    )

    if (userAffindaProfileResponse.data.user_affinda_profile?.length >= 0)
    {
      await this.$apollo.mutate({
        mutation: DELETE_AFFINDA_PROFILE,
        variables: {
          user_id: this.userId
        }
      })
      const userAffindaProfile = userAffindaProfileResponse.data.user_affinda_profile[0]
      await this.affindaClient.deleteDocument(userAffindaProfile?.document_id)
    }

    await storage.delete(this.resume);
    await this.updateResume(null);
  }

  async uploadFile(file: File): Promise<void> {
    try {
      const { ext, name } = splitFileNameExt(file.name);
      const s3Filename = `${name}-${uuid()}.${ext}`;
      
      await storage.save(s3Filename, file);
      if (this.resume) {
        await storage.delete(this.resume);
      }

      await this.updateResume(s3Filename);
    } catch (_) { }
  }

  async updateResume(resume: string | null): Promise<void> {
    try {
      await this.update(resume);

      const purpose = Boolean(resume) ? "updated" : "deleted";
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(`Your resume had been successfully ${purpose}.`)
      );

      this.refetch();
    } catch (e) {
      this.$eventBus.emit(
        NOTIFICATION, 
        Message.errorMessage(e.message)
      );
    }
  }

  async update(resume: string | null): Promise<void> {
    await this.$apollo.mutate({
      mutation: UPDATE_RESUME,
      variables: {
        userId: this.userId,
        resume,
      },
    });
  }

  refetch(): void {
    this.$apollo.queries.resume.refetch();
    this.$emit("update");
  }
}
